import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Lightbulb, Mic, Paperclip, TrendingUp, X, Zap } from "lucide-react";
import { cn } from "../../utils/function";

// CXO dashboard AI layer (Figma "AI Insights"): the Executive Summary card,
// "What you need to know" / "Suggested Improvements" notes under each chart,
// and "ask about this number" — any metric wrapped in <AskableValue> opens a
// small "Ask a question about this insight" box, which hands off to the AI
// Insights side panel. Answers come from POST /cxo-dashboard/ask; the
// insight cards come from each panel's own `ai_insights` field. Everything
// is hidden while GET /cxo-dashboard/ai-status says the org's AI is off.

export const AiIcon: React.FC<{ size?: number; className?: string }> = ({ size = 24, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path
      d="M13.5 3C13.904 3.00133 14.3007 3.02933 14.69 3.084C14.3796 3.45803 14.1648 3.90187 14.064 4.37733C13.9631 4.85279 13.9793 5.34562 14.1112 5.81343C14.243 6.28124 14.4865 6.71002 14.8208 7.06286C15.155 7.41571 15.57 7.68206 16.03 7.839L16.408 7.968C16.5528 8.01735 16.6844 8.09926 16.7926 8.20745C16.9007 8.31563 16.9827 8.44719 17.032 8.592L17.162 8.97C17.3192 9.42957 17.5856 9.84411 17.9383 10.178C18.2911 10.5119 18.7196 10.7551 19.1871 10.8868C19.6546 11.0185 20.1471 11.0348 20.6223 10.9341C21.0974 10.8335 21.541 10.6189 21.915 10.309C21.971 10.6983 21.9993 11.0953 22 11.5C22 12.6162 21.7801 13.7215 21.353 14.7528C20.9258 15.7841 20.2997 16.7211 19.5104 17.5104C18.7211 18.2997 17.7841 18.9258 16.7528 19.353C15.7215 19.7801 14.6162 20 13.5 20L13 20L13 21C13 21.2652 12.8946 21.5196 12.7071 21.7071C12.5196 21.8946 12.2652 22 12 22C6.984 22 2 18.087 2 11.5C2 9.24566 2.89553 7.08365 4.48959 5.48959C6.08365 3.89553 8.24566 3 10.5 3L13.5 3ZM8.5 10C8.10218 10 7.72064 10.158 7.43934 10.4393C7.15804 10.7206 7 11.1022 7 11.5C7 11.8978 7.15804 12.2794 7.43934 12.5607C7.72064 12.842 8.10218 13 8.5 13C8.89782 13 9.27936 12.842 9.56066 12.5607C9.84196 12.2794 10 11.8978 10 11.5C10 11.1022 9.84196 10.7206 9.56066 10.4393C9.27936 10.158 8.89782 10 8.5 10ZM15.5 10C15.1022 10 14.7206 10.158 14.4393 10.4393C14.158 10.7206 14 11.1022 14 11.5C14 11.8978 14.158 12.2794 14.4393 12.5607C14.7206 12.842 15.1022 13 15.5 13C15.8978 13 16.2794 12.842 16.5607 12.5607C16.842 12.2794 17 11.8978 17 11.5C17 11.1022 16.842 10.7206 16.5607 10.4393C16.2794 10.158 15.8978 10 15.5 10ZM20 1C20.2086 1.00008 20.4119 1.06539 20.5816 1.18679C20.7512 1.30818 20.8786 1.47959 20.946 1.677L21.076 2.055C21.376 2.934 22.066 3.625 22.946 3.925L23.323 4.054C23.5202 4.12157 23.6913 4.24908 23.8125 4.41869C23.9337 4.58831 23.9988 4.79155 23.9988 5C23.9988 5.20845 23.9337 5.41169 23.8125 5.58131C23.6913 5.75092 23.5202 5.87843 23.323 5.946L22.945 6.076C22.066 6.376 21.375 7.066 21.075 7.946L20.946 8.323C20.8784 8.5202 20.7509 8.69134 20.5813 8.81252C20.4117 8.93369 20.2085 8.99883 20 8.99883C19.7915 8.99883 19.5883 8.93369 19.4187 8.81252C19.2491 8.69134 19.1216 8.5202 19.054 8.323L18.924 7.945C18.7759 7.51114 18.5303 7.11699 18.2062 6.79283C17.882 6.46866 17.4879 6.2231 17.054 6.075L16.677 5.946C16.4798 5.87843 16.3087 5.75092 16.1875 5.58131C16.0663 5.41169 16.0012 5.20845 16.0012 5C16.0012 4.79155 16.0663 4.58831 16.1875 4.41869C16.3087 4.24908 16.4798 4.12157 16.677 4.054L17.055 3.924C17.934 3.624 18.625 2.934 18.925 2.054L19.054 1.677C19.1214 1.47959 19.2488 1.30818 19.4184 1.18679C19.5881 1.06539 19.7914 1.00008 20 1Z"
      fill="currentColor"
    />
  </svg>
);

// Purple badge version used on the insight cards.
const AiBadge: React.FC<{ size?: "sm" | "md" }> = ({ size = "sm" }) => (
  <span className={cn("rounded-full bg-violet-500 text-white flex items-center justify-center shrink-0", size === "md" ? "w-9 h-9" : "w-6 h-6")}>
    <AiIcon size={size === "md" ? 20 : 14} />
  </span>
);

// ── API ─────────────────────────────────────────────────────────────────────

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "https://api.connect.dalmlabs.com";

const authFetch = async (path: string, init?: RequestInit): Promise<{ status: number; body: any }> => {
  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      ...init,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("authToken")}`, ...(init?.headers || {}) },
    });
    const text = await res.text();
    let body: any = {};
    try {
      body = text ? JSON.parse(text) : {};
    } catch {
      body = {};
    }
    return { status: res.status, body };
  } catch {
    return { status: 0, body: {} };
  }
};

const detailText = (body: any, fallback: string): string => {
  const d = body?.detail ?? body?.message;
  if (typeof d === "string" && d) return d;
  if (Array.isArray(d) && d[0]?.msg) return String(d[0].msg);
  return fallback;
};

// The `ai_insights` field every CXO panel response carries:
// {enabled:false} | {enabled:true, cards, cached} | {enabled:true, error}.
export interface AiInsightCard {
  key: string;
  label: string;
  headline: string;
  body: string;
}

export interface AiInsightsPayload {
  enabled: boolean;
  cards?: AiInsightCard[];
  cached?: boolean;
  error?: string;
}

export interface PanelInsight {
  loading: boolean;
  data?: AiInsightsPayload;
}

export interface PanelRequest {
  endpoint: string;
  params: Record<string, string | number | undefined>;
}

const toQuery = (params: PanelRequest["params"]) =>
  new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== "").map(([k, v]) => [k, String(v)]))).toString();

// The pages load their panel data with ai_insights=false so the numbers show
// straight away; this then asks each panel again (in parallel) just for its
// ai_insights, which can take up to ~25s the first time a period is
// generated (cached by the backend after that). Skipped entirely while the
// org's AI switch is off.
export const usePanelInsights = (panels: Record<string, PanelRequest>): Record<string, PanelInsight> => {
  const { enabled } = useAiInsights();
  const [state, setState] = useState<Record<string, PanelInsight>>({});
  const key = JSON.stringify(panels);

  useEffect(() => {
    if (!enabled) {
      setState({});
      return;
    }
    let cancelled = false;
    const entries = Object.entries(panels);
    setState(Object.fromEntries(entries.map(([k]) => [k, { loading: true }])));
    entries.forEach(async ([k, req]) => {
      const { status, body } = await authFetch(`${req.endpoint}?${toQuery(req.params)}`);
      if (cancelled) return;
      const data: AiInsightsPayload =
        status === 200 && body?.ai_insights ? body.ai_insights : { enabled: true, error: detailText(body, "Couldn't load AI insights for this panel.") };
      setState((prev) => ({ ...prev, [k]: { loading: false, data } }));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return state;
};

// ── Context + side panel ────────────────────────────────────────────────────

export interface InsightContext {
  label: string;
  value?: string;
}

interface Message {
  id: number;
  from: "user" | "ai";
  text: string;
  context?: InsightContext | null;
  error?: boolean;
}

interface AiInsightsApi {
  // null while GET /cxo-dashboard/ai-status is still loading.
  enabled: boolean | null;
  ask: (question: string, context?: InsightContext | null) => void;
  open: () => void;
}

const AiInsightsCtx = createContext<AiInsightsApi | null>(null);

export const useAiInsights = () => {
  const api = useContext(AiInsightsCtx);
  if (!api) throw new Error("useAiInsights must be used inside <AiInsightsProvider>");
  return api;
};

const userAvatar = (): { src: string; initial: string } => {
  try {
    const u = JSON.parse(localStorage.getItem("userDetails") || "null");
    return { src: u?.profile_picture || u?.avatar || "", initial: (u?.first_name || u?.name || "U").charAt(0).toUpperCase() };
  } catch {
    return { src: "", initial: "U" };
  }
};

export const AiInsightsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [typing, setTyping] = useState(false);
  const [draft, setDraft] = useState("");
  const [context, setContext] = useState<InsightContext | null>(null);
  const idRef = useRef(0);
  const listRef = useRef<HTMLDivElement>(null);
  const avatar = userAvatar();

  const [enabled, setEnabled] = useState<boolean | null>(null);

  // GET /cxo-dashboard/ai-status — one switch per org, set by a global admin.
  // When it's off, the panel, the insight cards and "ask about this number"
  // are all hidden.
  useEffect(() => {
    authFetch("/cxo-dashboard/ai-status").then(({ status, body }) => setEnabled(status === 200 && body?.enabled === true));
  }, []);

  // POST /cxo-dashboard/ask. The model only sees the question text, so a
  // number the user clicked on is folded into it. History is the earlier
  // turns of this chat (the backend uses the last 10).
  const ask = async (question: string, ctx?: InsightContext | null) => {
    const text = question.trim();
    if (!text || typing || !enabled) return;
    const activeCtx = ctx === undefined ? context : ctx;
    // Show the metric chip only when the topic changes.
    const showChip = !!activeCtx && (activeCtx.label !== context?.label || activeCtx.value !== context?.value || messages.length === 0);
    setContext(activeCtx);
    setIsOpen(true);
    setMessages((m) => [...m, { id: ++idRef.current, from: "user", text, context: showChip ? activeCtx : null }]);
    setTyping(true);

    const full = (activeCtx ? `About "${activeCtx.label}"${activeCtx.value ? ` (${activeCtx.value})` : ""} on my dashboard: ${text}` : text).slice(0, 500);
    const history = messages
      .filter((m) => !m.error)
      .slice(-20)
      .map((m) => ({ role: m.from === "user" ? "user" : "assistant", content: m.text }));
    const { status, body } = await authFetch("/cxo-dashboard/ask", { method: "POST", body: JSON.stringify({ question: full.length >= 3 ? full : `${full}?`, history }) });

    let reply: Message;
    if (status === 200 && typeof body?.answer === "string" && body.answer) {
      reply = { id: ++idRef.current, from: "ai", text: body.answer };
    } else {
      if (status === 403) setEnabled(false);
      const fallback =
        status === 429
          ? "Too many questions. Try again in a minute."
          : status === 403
            ? "AI insights aren't enabled for your organization."
            : status === 0
              ? "Couldn't reach the assistant. Check your connection and try again."
              : "The assistant is unavailable right now. Try again shortly.";
      reply = { id: ++idRef.current, from: "ai", text: detailText(body, fallback), error: true };
    }
    setMessages((m) => [...m, reply]);
    setTyping(false);
  };

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, typing]);

  const send = () => {
    if (draft.trim().length < 3 || typing) return;
    ask(draft);
    setDraft("");
  };

  return (
    <AiInsightsCtx.Provider value={{ enabled, ask, open: () => setIsOpen(true) }}>
      {children}

      {enabled && isOpen && <div className="fixed inset-0 z-40 bg-black/20 sm:bg-transparent" onClick={() => setIsOpen(false)} />}
      <aside
        className={cn(
          "fixed top-0 right-0 z-50 h-screen w-full sm:w-[400px] bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 shadow-2xl flex flex-col transition-transform duration-300",
          enabled && isOpen ? "translate-x-0" : "translate-x-full"
        )}
        aria-hidden={!(enabled && isOpen)}
      >
        <div className="flex items-center justify-between px-5 py-4 bg-[#FFCB05] text-[#3A3428]">
          <h2 className="flex items-center gap-2 text-xl font-semibold">
            <AiIcon size={30} /> AI Insights
          </h2>
          <button type="button" onClick={() => setIsOpen(false)} aria-label="Close AI Insights" className="p-1 rounded-md hover:bg-black/10">
            <X size={22} />
          </button>
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center px-6 text-gray-500 dark:text-gray-400">
              <AiIcon size={36} className="text-[#3A3428] dark:text-amber-300" />
              <p className="mt-3 text-sm font-medium text-gray-700 dark:text-gray-200">Ask anything about your dashboard</p>
              <p className="mt-1 text-xs">Or click any number on the dashboard to ask about it.</p>
              <div className="mt-5 flex flex-col gap-2 w-full">
                {["Which region had the best ROAS in this period?", "Which campaigns should I put more budget into?", "Why did my cost per result change?"].map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => ask(q, null)}
                    className="text-left text-xs rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) =>
            m.from === "user" ? (
              <div key={m.id} className="flex flex-col items-end gap-2">
                {m.context && (
                  <span className="mr-11 inline-flex items-center gap-1.5 rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-1 text-xs text-gray-700 dark:text-gray-200">
                    {m.context.label}
                    {m.context.value && <span className="font-semibold text-violet-600 dark:text-violet-400 underline underline-offset-2">{m.context.value}</span>}
                  </span>
                )}
                <div className="flex items-end gap-2 max-w-[85%]">
                  <p className="rounded-2xl bg-gray-50 dark:bg-gray-800 shadow-sm px-4 py-2.5 text-sm text-gray-800 dark:text-gray-100">{m.text}</p>
                  {avatar.src ? (
                    <img src={avatar.src} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                  ) : (
                    <span className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-semibold flex items-center justify-center shrink-0">
                      {avatar.initial}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <p
                key={m.id}
                className={cn(
                  "rounded-2xl border shadow-sm px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap",
                  m.error
                    ? "border-rose-100 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300"
                    : "border-gray-100 dark:border-gray-800 text-gray-800 dark:text-gray-100"
                )}
              >
                {m.text}
              </p>
            )
          )}
          {typing && (
            <div className="w-16 rounded-2xl border border-gray-100 dark:border-gray-800 px-4 py-3 flex gap-1">
              {[0, 1, 2].map((i) => (
                <span key={i} className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: `${i * 120}ms` }} />
              ))}
            </div>
          )}
        </div>

        <div className="p-3">
          <div className="rounded-2xl border border-gray-300 dark:border-gray-700 px-3 pt-2.5 pb-2">
            <textarea
              rows={2}
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, 500))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Ask anything. . ."
              className="w-full resize-none bg-transparent outline-none text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400"
            />
            <div className="flex items-center justify-end gap-3">
              <button type="button" onClick={() => toast("Voice input is coming soon")} aria-label="Voice input" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <Mic size={20} />
              </button>
              <button type="button" onClick={() => toast("Attachments are coming soon")} aria-label="Attach a file" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <Paperclip size={20} />
              </button>
              <button
                type="button"
                onClick={send}
                disabled={draft.trim().length < 3 || typing}
                className="px-4 py-1.5 rounded-full bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-sm font-medium disabled:opacity-40"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </aside>
    </AiInsightsCtx.Provider>
  );
};

// ── "Ask about this number" ─────────────────────────────────────────────────

// Wraps a metric value: hover underlines it with the AI badge, click opens
// a small "Ask a question about this insight" box anchored under it.
export const AskableValue: React.FC<{ label: string; value: string; className?: string; children?: React.ReactNode }> = ({ label, value, className, children }) => {
  const { ask, enabled } = useAiInsights();
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  if (!enabled || !value || value === "—") return <span className={className}>{children ?? value}</span>;

  const submit = () => {
    ask(question.trim() || "What is this number?", { label, value });
    setQuestion("");
    setOpen(false);
  };

  return (
    <span className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Ask AI about this number"
        className={cn("group inline-flex items-center gap-1 rounded hover:text-violet-600 dark:hover:text-violet-400 hover:underline underline-offset-4 decoration-2", open && "text-violet-600 dark:text-violet-400 underline", className)}
      >
        {children ?? value}
        <AiIcon size={14} className={cn("text-violet-500 transition-opacity", open ? "opacity-100" : "opacity-0 group-hover:opacity-100")} />
      </button>
      {open && (
        <>
          <span className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <span className="absolute left-0 top-full mt-2 z-40 w-[260px] flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg p-1.5 pl-3 text-left">
            <input
              ref={inputRef}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
                if (e.key === "Escape") setOpen(false);
              }}
              placeholder="Ask a question about this insight"
              className="flex-1 min-w-0 bg-transparent outline-none text-xs font-normal text-gray-700 dark:text-gray-200 placeholder-gray-400"
            />
            <button type="button" onClick={submit} className="px-3 py-1.5 rounded-lg bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs font-semibold">
              Ask
            </button>
          </span>
        </>
      )}
    </span>
  );
};

// ── Executive Summary + insight notes ───────────────────────────────────────

const SUMMARY_META: Record<string, { icon: React.ElementType; cls: string }> = {
  top_opportunity: { icon: TrendingUp, cls: "bg-emerald-500" },
  watch: { icon: AlertTriangle, cls: "bg-amber-500" },
  recommended_action: { icon: Zap, cls: "bg-violet-500" },
};

const InsightError: React.FC<{ text: string }> = ({ text }) => (
  <p className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
    <AlertTriangle size={13} className="text-amber-500 shrink-0" /> {text}
  </p>
);

const Skeleton: React.FC<{ className?: string }> = ({ className }) => <span className={cn("block rounded bg-gray-200/80 dark:bg-gray-700/60 animate-pulse", className)} />;

// Top Opportunity / Watch / Recommended Action, from a panel's ai_insights.
// Renders nothing while the org's AI switch is off.
export const ExecutiveSummary: React.FC<{ period: string; insight?: PanelInsight }> = ({ period, insight }) => {
  const { enabled, open } = useAiInsights();
  if (!enabled || insight?.data?.enabled === false) return null;
  const loading = !insight || insight.loading;
  const cards = insight?.data?.cards || [];

  return (
    <div className="rounded-2xl border-2 border-[#FFCB05] bg-white dark:bg-gray-900 p-4 sm:p-5">
      <div className="flex items-center gap-3 mb-4">
        <AiBadge size="md" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Executive Summary</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Key insights from your ad performance ({period})</p>
        </div>
        <button
          type="button"
          onClick={open}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 px-3 py-1.5 text-xs font-semibold hover:opacity-90"
        >
          <AiIcon size={14} /> Ask AI
        </button>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-xl bg-gray-50 dark:bg-gray-800/60 p-4 space-y-2.5">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-3.5 w-4/5" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          ))}
          <p className="md:col-span-3 text-[11px] text-gray-400 dark:text-gray-500">Generating insights for this period. This can take a few seconds the first time.</p>
        </div>
      ) : insight?.data?.error ? (
        <InsightError text={insight.data.error} />
      ) : cards.length === 0 ? (
        <InsightError text="No insights for this period yet." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {cards.map((c) => {
            const meta = SUMMARY_META[c.key] || { icon: Lightbulb, cls: "bg-violet-500" };
            const Icon = meta.icon;
            return (
              <div key={c.key} className="rounded-xl bg-gray-50 dark:bg-gray-800/60 p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  <span className={cn("w-5 h-5 rounded flex items-center justify-center text-white", meta.cls)}>
                    <Icon size={12} />
                  </span>
                  {c.label}
                </p>
                <p className="mt-2 text-sm font-medium text-gray-800 dark:text-gray-200">{c.headline}</p>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{c.body}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// "What you need to know" + "Suggested improvements" under a chart/table,
// from that panel's ai_insights. Renders nothing while AI is off.
export const InsightNotes: React.FC<{ insight?: PanelInsight }> = ({ insight }) => {
  const { enabled } = useAiInsights();
  if (!enabled || insight?.data?.enabled === false) return null;
  const loading = !insight || insight.loading;
  const cards = insight?.data?.cards || [];
  const error = insight?.data?.error;

  const boxes = [
    { key: "what_you_need_to_know", title: "What you need to know", icon: <AiBadge /> },
    {
      key: "suggested_improvements",
      title: "Suggested improvements",
      icon: (
        <span className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-600 flex items-center justify-center shrink-0">
          <Lightbulb size={13} />
        </span>
      ),
    },
  ];

  if (!loading && (error || cards.length === 0)) {
    return (
      <div className="mt-4 rounded-xl border-2 border-[#FFE58A] dark:border-amber-500/40 bg-[#FFFDF5] dark:bg-amber-500/5 p-3.5">
        <InsightError text={error || "No insights for this period yet."} />
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-3">
      {boxes.map((box) => {
        const card = cards.find((c) => c.key === box.key);
        if (!loading && !card) return null;
        return (
          <div key={box.key} className="rounded-xl border-2 border-[#FFE58A] dark:border-amber-500/40 bg-[#FFFDF5] dark:bg-amber-500/5 p-3.5">
            <p className="flex items-center gap-2 text-xs font-semibold text-gray-900 dark:text-gray-100">
              {box.icon}
              {card?.label || box.title}
            </p>
            {loading ? (
              <div className="mt-2.5 space-y-1.5 pl-1">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-3/4" />
              </div>
            ) : (
              <p className="mt-2 pl-1 text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                <b>{card!.headline}</b> {card!.body}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};
